import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Clock, Save, Loader2, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Configuration modal for the "outside-business-hours" WhatsApp auto-reply.
 * Business hours are fixed to Europe/Bucharest but the open/close hour is
 * editable. Two Meta template names must be provided:
 *   - `template_with_order`    — for customers we could match to an order
 *   - `template_without_order` — everyone else
 * Weekends (Sat/Sun) are always considered closed.
 */
export default function WhatsAppAutoReplyModal({ onClose }) {
  const [cfg, setCfg] = useState({
    enabled: false, open_hour: 9, close_hour: 16,
    template_with_order: "", template_without_order: "", template_morning: "",
  });
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([
          axios.get(`${API}/whatsapp/auto-reply/config`),
          axios.get(`${API}/whatsapp/templates?meta_status=APPROVED`).catch(() => ({ data: { items: [] } })),
        ]);
        setCfg(c.data);
        // Accept both `status` (Meta's own field, actual value returned
        // by the backend) and legacy `meta_status` — some older rows in
        // the templates cache use the latter.
        const items = t.data.items || [];
        setTpls(items.filter((x) => (x.status || x.meta_status) === "APPROVED"));
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/whatsapp/auto-reply/config`, cfg);
      try { (await import("sonner")).toast.success("Salvat"); } catch { /* ok */ }
      onClose();
    } catch (e) {
      try { (await import("sonner")).toast.error(e?.response?.data?.detail || "Eroare"); } catch { /* ok */ }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4" data-testid="wa-autoreply-modal">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
              <Clock className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-100">Auto-reply în afara programului</h2>
              <p className="text-[11px] text-zinc-500">Program lucru: L-V între orele setate mai jos, S-D închis complet.</p>
            </div>
          </div>
          <button data-testid="wa-autoreply-close" onClick={onClose} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5 text-sm text-zinc-100">
            <label className="flex items-center gap-3">
              <input data-testid="wa-autoreply-enabled" type="checkbox" checked={cfg.enabled}
                onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))}
                className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-emerald-500" />
              <span>Activat</span>
              <span className="ml-auto text-[10px] text-zinc-500">Fusul orar: Europe/Bucharest, S-D închis mereu</span>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-zinc-300">Deschis de la (oră)</label>
                <input data-testid="wa-autoreply-open" type="number" min={0} max={23} value={cfg.open_hour}
                  onChange={(e) => setCfg((c) => ({ ...c, open_hour: parseInt(e.target.value || "0", 10) }))}
                  className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-zinc-300">Închis de la (oră)</label>
                <input data-testid="wa-autoreply-close" type="number" min={1} max={24} value={cfg.close_hour}
                  onChange={(e) => setCfg((c) => ({ ...c, close_hour: parseInt(e.target.value || "0", 10) }))}
                  className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono" />
              </div>
            </div>

            <div className="space-y-3">
              <TemplatePicker label="Instant — client CU comandă (când clientul scrie afară de program)"
                value={cfg.template_with_order} tpls={tpls}
                onChange={(v) => setCfg((c) => ({ ...c, template_with_order: v }))}
                testid="wa-autoreply-tpl-with-order" />
              <TemplatePicker label="Instant — client FĂRĂ comandă"
                value={cfg.template_without_order} tpls={tpls}
                onChange={(v) => setCfg((c) => ({ ...c, template_without_order: v }))}
                testid="wa-autoreply-tpl-without-order" />
              <TemplatePicker label={'Luni dimineața la 09:00 — template salut (ex. „Bună dimineața!")'}
                value={cfg.template_morning} tpls={tpls}
                onChange={(v) => setCfg((c) => ({ ...c, template_morning: v }))}
                testid="wa-autoreply-tpl-morning" />
              <p className="text-[10px] text-zinc-500 pl-1">
                Dacă lași câmpul „Luni dimineața&rdquo; gol, sistemul re-folosește template-urile de sus. Dacă îl completezi, exact acel template va fi trimis L-V la 09:00 tuturor clienților care au scris în afara programului.
              </p>
            </div>

            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100">
              <strong>Reguli Meta pe care nu le putem ocoli:</strong>
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                <li>Auto-reply-ul trimis se numără ca o interacțiune din partea noastră — <strong>NU resetează fereastra de 24h</strong>. Doar un mesaj nou de la client resetează.</li>
                <li>Dacă un client scrie vineri seara și nu răspunzi până luni dimineața, fereastra 24h se închide. Sistemul poate trimite un template automat luni dimineața la 09:00 pentru a redeschide conversația — spune-mi dacă vrei să adaug și acel job.</li>
                <li>Contorul „mesaje necitite&rdquo; NU se resetează după auto-reply — mesajul rămâne highlighted până răspunzi tu manual.</li>
              </ul>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 bg-zinc-900/50 px-5 py-3">
          <button onClick={onClose} className="rounded-md border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700">Închide</button>
          <button data-testid="wa-autoreply-save" disabled={saving || loading} onClick={save}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-400 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salvează
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplatePicker({ label, value, tpls, onChange, testid }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-zinc-300">{label}</label>
      <div className="flex items-center gap-2">
        <MessageCircle className="h-3.5 w-3.5 text-emerald-400" />
        <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm">
          <option value="">— alege template APPROVED —</option>
          {tpls.map((t) => (
            <option key={t.id || t.name || t.meta_name} value={t.meta_name || t.name}>
              {t.name || t.meta_name}{t.language ? ` (${t.language})` : ""}
            </option>
          ))}
        </select>
      </div>
      {value && !tpls.some((t) => (t.meta_name || t.name) === value) && (
        <div className="mt-1 text-[10px] text-amber-400">
          ⚠️ Template-ul „{value}&rdquo; nu apare în lista APPROVED — verifică că e aprobat de Meta.
        </div>
      )}
    </div>
  );
}
